// @flow
import PATHS, { HOUSEHOLD_PATHS } from 'constants/paths';
import { SectionSteps } from 'hooks/sectionsProgress/types';
import { FormattedMessage } from 'react-intl';
import { DESIGNATION_FIELD_NAMES } from 'pages/AffordaHousehold/AffordaHousehold.Designation.constants';
import { MINORS_RELATIONSHIP_FIELD_NAMES } from 'pages/AffordaHousehold/AffordaHousehold.MinorsRelationship.constants';
import { FULL_TIME_STUDENT_FIELD_NAMES } from 'pages/AffordaHousehold/AffordaHousehold.FullTimeStudent.constants';
import { ADDITIONAL_MINORS_FIELD_NAMES } from 'pages/AffordaHousehold/AffordaHousehold.AdditionalMinors.constants';
import { HUD_HOME_STUDENT_STATUS_FIELD_NAMES } from 'pages/AffordaHousehold/AffordaHousehold.HudHomeStudentStatus.constants';
import { DEMOGRAPHICS_FIELD_NAMES } from 'pages/AffordaHousehold/AffordaHousehold.Demographics.constants';
import {
  PREFERENCE_PAGE_PATHS,
  PREFERENCES_FIELD_NAMES,
} from 'pages/AffordaHousehold/AffordaHousehold.Preferences.constants';
import { YES_NO_OPTIONS } from 'utils/constants';

export const BUTTON_GROUP_SX = {
  flexWrap: 'wrap',
  width: '100%',
  '& > button': {
    width: { xs: 'calc(100% - 4px)', sm: 'calc(50% - 4px)' },
  },
};

// `BUTTON_GROUP_SX` pins every button to half the row, which is right for a Yes/No pair but wraps
// a third option onto its own line. These groups carry three, so the buttons size to their content
// and share the row instead.
export const THREE_OPTION_BUTTON_GROUP_SX = {
  flexWrap: 'nowrap',
  width: '100%',
  '& > button': { flex: '1 1 auto', width: 'auto', minWidth: 0 },
};

export const NOT_WISH_TO_PROVIDE = 'notWishToProvide';

export const YES_NO_OPTIONS_WITH_NOT_WISH_TO_PROVIDE = [
  ...YES_NO_OPTIONS,
  {
    label: <FormattedMessage id="app.option.notWishToProvide" />,
    value: NOT_WISH_TO_PROVIDE,
  },
];

// Only adding optional forms steps fields here. Can change this later if needed.
export const HOUSEHOLD_PATHS_TO_FIELD_NAMES_MAP = {
  [HOUSEHOLD_PATHS.DESIGNATION]: DESIGNATION_FIELD_NAMES,
  [HOUSEHOLD_PATHS.MINOR_RELATIONSHIP]: MINORS_RELATIONSHIP_FIELD_NAMES,
  [HOUSEHOLD_PATHS.FULL_TIME_STUDENT]: FULL_TIME_STUDENT_FIELD_NAMES,
  [HOUSEHOLD_PATHS.HUD_HOME_STUDENT_STATUS]:
    HUD_HOME_STUDENT_STATUS_FIELD_NAMES,
  [HOUSEHOLD_PATHS.ADDITIONAL_MINORS]: ADDITIONAL_MINORS_FIELD_NAMES,
  [HOUSEHOLD_PATHS.DEMOGRAPHICS]: DEMOGRAPHICS_FIELD_NAMES,
  // Page 1 only, and over all ten preferences. Cleanup deletes the whole `preferences` key on its
  // first field match, and it runs for every step whose `required` is not `true` — so an entry on
  // pages 2 to 4 would wipe every answer while a property with five preferences left pages 3 and 4
  // unrequired. Page 1 is unrequired exactly when the property asks nothing, which is the case
  // cleanup is for.
  [HOUSEHOLD_PATHS.PREFERENCES_1]: PREFERENCES_FIELD_NAMES,
};

export const JOINT_CUSTODY_MAP = {
  no: {
    messageId: 'app.no',
    text: <FormattedMessage id="app.no" />,
    value: 'no',
  },
  yes50: {
    messageId: 'afforda.minorsRelationships.jointCustody.yes50',
    text: (
      <FormattedMessage id="afforda.minorsRelationships.jointCustody.yes50" />
    ),
    value: 'yes50',
  },
  yesLessThan50: {
    messageId: 'afforda.minorsRelationships.jointCustody.yesLessThan50',
    text: (
      <FormattedMessage id="afforda.minorsRelationships.jointCustody.yesLessThan50" />
    ),
    value: 'yesLessThan50',
  },
};

export const HOUSEHOLD_SECTION_NAMES = {
  MEMBERS: 'members',
  DESIGNATION: 'designation',
  MARITAL_STATUS: 'maritalStatus',
  MINOR_RELATIONSHIP: 'minorsRelationship',
  STUDENT_STATUS: 'studentStatus',
  FULL_TIME_STUDENT: 'fullTimeStudent',
  RD_STUDENT_STATUS: 'rdStudentStatus',
  HUD_HOME_STUDENT_STATUS: 'hudHomeStudentStatus',
  ADDITIONAL_MINORS: 'additionalMinors',
  // Shared by all four preference pages: it is the form-state key their one form writes to, and
  // what cleanup matches on to find those answers.
  PREFERENCES: 'preferences',
  DEMOGRAPHICS: 'demographics',
  LIVE_IN_AIDE: 'liveInAide',
  SUMMARY: 'summary',
  INVITE: 'invite',
};

export const HOUSEHOLD_STEPS = ({
  canInviteOthers,
  hasMoreThanOneAdult,
  hasMinors,
  shouldShowRdStudentQuestions,
  shouldShowHudHomeStudentQuestions,
  isLIHTCProperty,
  allMembersFullTime,
  isAffordableUser,
  shouldShowDemographics,
  preferencePageCount,
}): SectionSteps => {
  return [
    {
      name: HOUSEHOLD_PATHS.MEMBERS,
      sectionName: HOUSEHOLD_SECTION_NAMES.MEMBERS,
      path: `${PATHS.HOUSEHOLD}/${HOUSEHOLD_PATHS.MEMBERS}`,
      required: true,
    },
    {
      name: HOUSEHOLD_PATHS.DESIGNATION,
      sectionName: HOUSEHOLD_SECTION_NAMES.DESIGNATION,
      path: `${PATHS.HOUSEHOLD}/${HOUSEHOLD_PATHS.DESIGNATION}`,
      required: hasMoreThanOneAdult,
      optional: true,
    },
    {
      name: HOUSEHOLD_PATHS.MARITAL_STATUS,
      sectionName: HOUSEHOLD_SECTION_NAMES.MARITAL_STATUS,
      path: `${PATHS.HOUSEHOLD}/${HOUSEHOLD_PATHS.MARITAL_STATUS}`,
      required: true,
    },
    {
      name: HOUSEHOLD_PATHS.MINOR_RELATIONSHIP,
      sectionName: HOUSEHOLD_SECTION_NAMES.MINOR_RELATIONSHIP,
      path: `${PATHS.HOUSEHOLD}/${HOUSEHOLD_PATHS.MINOR_RELATIONSHIP}`,
      required: isAffordableUser ? hasMinors : hasMoreThanOneAdult && hasMinors,
      optional: true,
    },
    {
      name: HOUSEHOLD_PATHS.STUDENT_STATUS,
      sectionName: HOUSEHOLD_SECTION_NAMES.STUDENT_STATUS,
      path: `${PATHS.HOUSEHOLD}/${HOUSEHOLD_PATHS.STUDENT_STATUS}`,
      required: isAffordableUser,
    },
    {
      name: HOUSEHOLD_PATHS.FULL_TIME_STUDENT,
      sectionName: HOUSEHOLD_SECTION_NAMES.FULL_TIME_STUDENT,
      path: `${PATHS.HOUSEHOLD}/${HOUSEHOLD_PATHS.FULL_TIME_STUDENT}`,
      required: isAffordableUser && isLIHTCProperty && allMembersFullTime,
      optional: true,
    },
    {
      name: HOUSEHOLD_PATHS.RD_STUDENT_STATUS,
      sectionName: HOUSEHOLD_SECTION_NAMES.RD_STUDENT_STATUS,
      path: `${PATHS.HOUSEHOLD}/${HOUSEHOLD_PATHS.RD_STUDENT_STATUS}`,
      required: isAffordableUser && shouldShowRdStudentQuestions,
      optional: true,
    },
    {
      name: HOUSEHOLD_PATHS.HUD_HOME_STUDENT_STATUS,
      sectionName: HOUSEHOLD_SECTION_NAMES.HUD_HOME_STUDENT_STATUS,
      path: `${PATHS.HOUSEHOLD}/${HOUSEHOLD_PATHS.HUD_HOME_STUDENT_STATUS}`,
      required: isAffordableUser && shouldShowHudHomeStudentQuestions,
      optional: true,
    },
    {
      name: HOUSEHOLD_PATHS.ADDITIONAL_MINORS,
      sectionName: HOUSEHOLD_SECTION_NAMES.ADDITIONAL_MINORS,
      path: `${PATHS.HOUSEHOLD}/${HOUSEHOLD_PATHS.ADDITIONAL_MINORS}`,
      required: isAffordableUser,
    },
    // Four fixed pages, of which the property's preference count decides how many are required.
    // A page beyond that count renders no question, and a page with no question mounts no field,
    // which leaves its form permanently invalid and its Continue button dead — so an unused page
    // has to be excluded here rather than left to render nothing.
    ...PREFERENCE_PAGE_PATHS.map((name, index) => ({
      name,
      sectionName: HOUSEHOLD_SECTION_NAMES.PREFERENCES,
      path: `${PATHS.HOUSEHOLD}/${name}`,
      required: index < preferencePageCount,
      ...(index === 0 && { optional: true }),
    })),
    {
      name: HOUSEHOLD_PATHS.DEMOGRAPHICS,
      sectionName: HOUSEHOLD_SECTION_NAMES.DEMOGRAPHICS,
      path: `${PATHS.HOUSEHOLD}/${HOUSEHOLD_PATHS.DEMOGRAPHICS}`,
      required: shouldShowDemographics,
      optional: true,
    },
    {
      name: HOUSEHOLD_PATHS.SUMMARY,
      sectionName: HOUSEHOLD_SECTION_NAMES.SUMMARY,
      path: `${PATHS.HOUSEHOLD}/${HOUSEHOLD_PATHS.SUMMARY}`,
      required: true,
    },
    {
      name: HOUSEHOLD_PATHS.INVITE,
      sectionName: HOUSEHOLD_SECTION_NAMES.INVITE,
      path: `${PATHS.HOUSEHOLD}/${HOUSEHOLD_PATHS.INVITE}`,
      optional: true,
      required: canInviteOthers,
    },
  ];
};
